import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Inbox as InboxIcon,
  Mail,
  Plus,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  Lock,
  Server,
} from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import EmptyState from "@/components/EmptyState";
import StatusBadge from "@/components/StatusBadge";
import ConnectSMTPModal from "@/components/ConnectSMTPModal";

export default function Inboxes() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const [menuOpen, setMenuOpen] = useState(false);
  const [smtpOpen, setSmtpOpen] = useState(false);
  const menuRef = useRef(null);
  const navigate = useNavigate();
  const { isAdmin } = useAuth();

  const load = useCallback(() => {
    setLoading(true);
    api
      .get("/inboxes")
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Failed to load inboxes"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const p = params.get("gmail");
    if (p === "connected") {
      toast.success("Gmail connected");
      params.delete("gmail");
      setParams(params, { replace: true });
    } else if (p === "error") {
      toast.error("Gmail connection failed");
      params.delete("gmail");
      setParams(params, { replace: true });
    } else if (p === "expired") {
      toast.error("OAuth state expired - please retry");
      params.delete("gmail");
      setParams(params, { replace: true });
    }
  }, [params, setParams]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const connectGmailOAuth = async () => {
    setMenuOpen(false);
    // Strike 2 — Google OAuth connect is admin-gated (CASA verification pending).
    if (!isAdmin) {
      toast.info(
        "Gmail OAuth is coming soon. For now, connect via Gmail App Password or SMTP.",
      );
      return;
    }
    try {
      const res = await api.get("/oauth/gmail/connect");
      window.location.href = res.data.auth_url;
    } catch (e) {
      const msg =
        e?.response?.data?.detail ||
        "Could not start Gmail OAuth. You can also connect via Gmail App Password (no Google Cloud setup needed).";
      toast.error(msg);
    }
  };

  const openSmtp = () => {
    setMenuOpen(false);
    setSmtpOpen(true);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-neutral-500 font-mono">
            Sending mailboxes
          </div>
          <h1 className="font-display text-4xl font-semibold tracking-tight mt-2">
            Inboxes
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={load}
            data-testid="refresh-inboxes-button"
            className="flex items-center gap-2 px-3 py-2 border border-[#27272a] rounded-md text-sm hover:bg-[#161616] transition-colors"
          >
            <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} />
            Refresh
          </button>
          <div className="relative" ref={menuRef}>
            <button
              onClick={() => setMenuOpen((s) => !s)}
              data-testid="connect-menu-button"
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-md text-sm font-medium hover:bg-neutral-200 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Connect mailbox
              <ChevronDown className="w-3.5 h-3.5" strokeWidth={1.8} />
            </button>
            {menuOpen && (
              <div className="absolute right-0 top-full mt-2 w-72 bg-[#121212] border border-[#27272a] rounded-lg z-30 overflow-hidden">
                <MenuItem
                  icon={Mail}
                  title="Gmail (OAuth)"
                  desc="Best for Gmail - needs your Google Cloud Client ID/Secret in backend .env"
                  onClick={connectGmailOAuth}
                  testId="connect-gmail-oauth"
                  disabled={!isAdmin}
                  badge={!isAdmin ? "Coming soon" : null}
                />
                <MenuItem
                  icon={Lock}
                  title="Gmail (App Password)"
                  desc="Quickest path - no Google Cloud setup. 2FA + App Password required."
                  onClick={openSmtp}
                  testId="connect-gmail-smtp"
                />
                <MenuItem
                  icon={Server}
                  title="Outlook / SMTP / Custom"
                  desc="Any provider with SMTP+IMAP credentials"
                  onClick={openSmtp}
                  testId="connect-smtp-custom"
                  last
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div className="text-neutral-500 text-sm font-mono">Loading…</div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={InboxIcon}
          title="No mailboxes connected"
          description="Connect your sending mailbox via Gmail OAuth, Gmail App Password, or any SMTP/IMAP provider. You can connect multiple - campaigns rotate sends across them."
          action={
            <button
              onClick={() => setMenuOpen(true)}
              data-testid="empty-connect-button"
              className="flex items-center gap-2 px-4 py-2 bg-white text-black rounded-md text-sm font-medium"
            >
              <Plus className="w-4 h-4" strokeWidth={1.8} />
              Connect mailbox
            </button>
          }
        />
      ) : (
        <div className="border border-[#1a1a1a] bg-[#0d0d0d] rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="text-[10px] uppercase tracking-[0.18em] text-neutral-500 font-mono">
              <tr className="border-b border-[#1a1a1a]">
                <th className="text-left px-6 py-3">Email</th>
                <th className="text-left px-6 py-3">Method</th>
                <th className="text-left px-6 py-3">Status</th>
                <th className="text-right px-6 py-3">Daily limit</th>
                <th className="text-right px-6 py-3">Sent today</th>
                <th className="text-right px-6 py-3">Health</th>
                <th />
              </tr>
            </thead>
            <tbody className="font-mono text-sm">
              {items.map((i) => {
                const used = (i.sent_today || 0) / Math.max(1, i.daily_limit);
                const healthLabel =
                  i.health_score >= 80
                    ? "healthy"
                    : i.health_score >= 50
                    ? "warning"
                    : "critical";
                const ct = i.connection_type || "gmail_oauth";
                const ctLabel =
                  ct === "gmail_oauth"
                    ? "Gmail OAuth"
                    : ct === "gmail_smtp"
                    ? "Gmail SMTP"
                    : "SMTP";
                return (
                  <tr
                    key={i.id}
                    onClick={() => navigate(`/inboxes/${i.id}`)}
                    className="border-b border-[#1a1a1a] hover:bg-[#111] cursor-pointer"
                    data-testid={`inbox-row-${i.id}`}
                  >
                    <td className="px-6 py-3 text-white">{i.email_address}</td>
                    <td className="px-6 py-3 text-neutral-400">{ctLabel}</td>
                    <td className="px-6 py-3">
                      <StatusBadge status={i.status} />
                    </td>
                    <td className="px-6 py-3 text-right text-neutral-300">
                      {i.daily_limit}
                    </td>
                    <td className="px-6 py-3 text-right">
                      <div className="inline-flex items-center gap-2 justify-end">
                        <span>{i.sent_today || 0}</span>
                        <div className="w-16 h-1 rounded-full bg-[#1f1f1f] overflow-hidden">
                          <div
                            className="h-full bg-white"
                            style={{ width: `${Math.min(100, used * 100)}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <StatusBadge status={healthLabel} />
                    </td>
                    <td className="px-6 py-3 text-right">
                      <ChevronRight
                        className="w-4 h-4 text-neutral-500"
                        strokeWidth={1.5}
                      />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <ConnectSMTPModal
        open={smtpOpen}
        onClose={() => setSmtpOpen(false)}
        onSuccess={load}
      />
    </div>
  );
}

function MenuItem({ icon: Icon, title, desc, onClick, testId, last, disabled, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      aria-disabled={disabled ? "true" : undefined}
      className={`w-full text-left px-4 py-3 transition-colors flex items-start gap-3 ${
        last ? "" : "border-b border-[#1a1a1a]"
      } ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-[#1a1a1a]"}`}
    >
      <Icon className="w-4 h-4 text-white mt-0.5" strokeWidth={1.5} />
      <div className="flex-1 min-w-0">
        <div className="text-sm text-white flex items-center gap-2">
          {title}
          {badge && (
            <span
              data-testid={`${testId}-badge`}
              className="text-[9px] font-mono uppercase tracking-[0.15em] text-amber-300 border border-amber-400/30 bg-amber-400/10 rounded px-1.5 py-0.5"
            >
              {badge}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-500 mt-0.5 leading-snug">{desc}</div>
      </div>
    </button>
  );
}
